import React, { forwardRef, useCallback } from "react";
import PropTypes from "prop-types";
import cn from "classnames";
import TableCell from "../TableCell";
import styles from "./TablePagination.module.scss";
import Icon from "../../Icon";

/**
 * A `TableCell` based component for placing inside `TableFooter` for pagination.
 */
const TablePagination = forwardRef(
  (
    {
      backIconButtonProps,
      nextIconButtonProps,
      toFirstButtonProps,
      toLastButtonProps,
      className,
      activePageClassName,
      colSpan: colSpanProp,
      component: Component,
      totalPageCount,
      onChangePage,
      page,
      RowsPerPageComponent,
      ...rest
    },
    ref
  ) => {
    let colSpan;

    if (Component === TableCell || Component === "td") {
      colSpan = colSpanProp || 1000; // col-span over everything
    }

    const handleToFirstButtonClick = useCallback(
      event => {
        onChangePage(1, event);
      },
      [onChangePage]
    );

    const handleToLastButtonClick = useCallback(
      event => {
        onChangePage(totalPageCount, event);
      },
      [onChangePage, totalPageCount]
    );

    const handleBackButtonClick = useCallback(
      event => {
        onChangePage(page - 1, event);
      },
      [onChangePage, page]
    );

    const handleNextButtonClick = useCallback(
      event => {
        onChangePage(page + 1, event);
      },
      [onChangePage, page]
    );

    const nextPageActive = page < totalPageCount;
    const iconSize = 16;

    return (
      <Component ref={ref} className={cn(styles.container, className)} colSpan={colSpan} {...rest}>
        <div className={styles.pageContainer}>
          {RowsPerPageComponent || ""}
          <div
            className={cn(styles.toBeginningButton, { [styles.disabled]: page === 1 })}
            onClick={handleToFirstButtonClick}
            {...toFirstButtonProps}
            role="button"
            tabIndex={-1}
          >
            <Icon icon="previous" size={iconSize} />
          </div>
          <div
            className={cn(styles.backButton, { [styles.disabled]: page === 1 })}
            onClick={handleBackButtonClick}
            {...backIconButtonProps}
            role="button"
            tabIndex={-1}
          >
            <Icon icon="circle-left" size={iconSize} />
          </div>
          <div
            className={cn(styles.page, {
              [styles.active]: nextPageActive,
              [activePageClassName]: nextPageActive && activePageClassName
            })}
          >
            {!nextPageActive ? page - 1 : page}
          </div>
          <div
            className={cn(styles.page, {
              [styles.active]: !nextPageActive,
              [activePageClassName]: !nextPageActive && activePageClassName
            })}
          >
            {!nextPageActive ? page : page + 1}
          </div>
          <div>...</div>
          <div className={styles.page}>{totalPageCount}</div>
          <div
            className={cn(styles.nextButton, { [styles.disabled]: !nextPageActive })}
            onClick={handleNextButtonClick}
            {...nextIconButtonProps}
            role="button"
            tabIndex={-1}
          >
            <Icon icon="circle-right" size={iconSize} />
          </div>
          <div
            className={cn(styles.toLastButton, { [styles.disabled]: page >= totalPageCount })}
            onClick={handleToLastButtonClick}
            {...toLastButtonProps}
            role="button"
            tabIndex={-1}
          >
            <Icon icon="next" size={iconSize} />
          </div>
        </div>
      </Component>
    );
  }
);

TablePagination.displayName = "TablePagination";

TablePagination.defaultProps = {
  toFirstButtonProps: {},
  backIconButtonProps: {},
  nextIconButtonProps: {},
  toLastButtonProps: {},
  className: undefined,
  activePageClassName: undefined,
  colSpan: undefined,
  component: TableCell,
  RowsPerPageComponent: undefined
};

TablePagination.propTypes = {
  /**
   * Props applied to the go to first page Icon component.
   *
   * */
  toFirstButtonProps: PropTypes.object,
  /**
   * Props applied to the back arrow [`IconButton`](/api/icon-button/) component.
   */
  backIconButtonProps: PropTypes.object,
  /**
   * Props applied to the next arrow [`IconButton`](/api/icon-button/) element.
   */
  nextIconButtonProps: PropTypes.object,
  /**
   * Props applied to the last arrow [`IconButton`](/api/icon-button/) element.
   */
  toLastButtonProps: PropTypes.object,
  /**
   * Override or extend the styles applied to the component.
   */
  className: PropTypes.string,
  /**
   * Override or extend the styles of active Page.
   */
  activePageClassName: PropTypes.string,
  /**
   * @ignore
   */
  colSpan: PropTypes.number,
  /**
   * The component used for the root node.
   * Either a string to use a DOM element or a component.
   */
  component: PropTypes.elementType,
  /**
   * The total number of rows.
   */
  totalPageCount: PropTypes.number.isRequired,
  /**
   * Callback fired when the page is changed.
   *
   * @param {object} event The event source of the callback.
   * @param {number} page The page selected.
   */
  onChangePage: PropTypes.func.isRequired,
  /**
   * The zero-based index of the current page.
   */
  page: PropTypes.number.isRequired,
  /**
   * Could be <RowsPerPage/> component or any component you want.
   */
  RowsPerPageComponent: PropTypes.node
};

export default TablePagination;
